import AddIcon from "@mui/icons-material/Add";
import {
	IconButton,
	Box,
	Button,
	Typography,
	Modal,
	LinearProgress,
	Snackbar,
	Alert,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { MapViewContext } from "../../contexts/MapViewContext";
import Graphic from "@arcgis/core/Graphic";
import { gpURL } from "../../constants";
import * as geoprocessor from "@arcgis/core/rest/geoprocessor";
import esriRequest from "@arcgis/core/request";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";

import CityContext from "../../contexts/CityContext";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import Color from "@arcgis/core/Color";
import FeatureSet from "@arcgis/core/rest/support/FeatureSet";
import JobInfo from "@arcgis/core/rest/support/JobInfo";
import EvaluatorContext from "../../contexts/EvaluatorContext";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import { AlignmentContext } from "../../contexts/AlignmentContext";
import { trackEvent } from "../../hooks/useAnalytics";

interface GPParams {
	INPUT_PARK_FS?: Graphic[];
	INPUT_TRAILS_FS?: Graphic[];
	INPUT_PLACE_ID?: string;
}

const style = {
	position: "absolute" as "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 400,
	bgcolor: "background.paper",
	boxShadow: 24,
	p: 4,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
};

// @ts-ignore
const polygonSymbol: SimpleFillSymbol = {
	type: "simple-fill",
	style: "diagonal-cross",
	color: new Color([0, 153, 51, 0.7]),
	outline: new SimpleLineSymbol({
		style: "dash",
		color: new Color([0, 0, 0]),
		width: 2,
	}),
};

// @ts-ignore
const invalidPolygonSymbol: SimpleFillSymbol = {
	type: "simple-fill",
	style: "cross",
	color: new Color([255, 0, 0]),
	outline: new SimpleLineSymbol({
		style: "dash",
		color: new Color([255, 0, 0]),
		width: 2,
	}),
};

// @ts-ignore
const polylineSymbol: SimpleLineSymbol = {
	type: "simple-line",
	style: "dash",
	color: new Color([255, 165, 0]),
	width: 2,
};

// @ts-ignore
const invalidPolylineSymbol: SimpleLineSymbol = {
	type: "simple-line",
	style: "dash",
	color: new Color([235, 79, 79]),
	width: 2,
};

// @ts-ignore
const walkAreaSymbol: SimpleFillSymbol = {
	type: "simple-fill",
	color: new Color([221, 232, 128, 0.7]),
	outline: new SimpleLineSymbol({
		style: "dash",
		color: new Color([175, 189, 66]),
		width: 2,
	}),
};

const ParkTrailButtons = () => {
	const { city } = useContext(CityContext);
	const view = useContext(MapViewContext);
	const {
		gpArray,
		gpJson,
		parkArray,
		trailArray,
		setGpArray,
		setGpJson,
		setParkArray,
		setTrailArray,
	} = useContext(EvaluatorContext);
	const { setAlignment } = useContext(AlignmentContext);

	const graphicsLayer: any = view?.map.findLayerById("sketch-layer");
	const walkAreaGL: any = view?.map.findLayerById("walk-layer");

	let sketchVM: SketchViewModel;

	const [open, setOpen] = React.useState(false);
	const [jobMessage, setJobMessage] = React.useState<string>("");
	const [graphicsLength, setGraphicsLength] = useState<number>(0);
	const [gpProgress, setGpProgress] = useState<number>(0);
	const [gpSuccess, setGPSuccess] = React.useState(false);
	const [gpFailure, setGPFailure] = React.useState(false);
	const [gpCancel, setGPCancel] = React.useState(false);
	const [gpStartTime, setGpStartTime] = React.useState<any>(null);
	const [gpJobId, setGpJobId] = React.useState<string>("");
	const [executed, setExecuted] = React.useState(false);
	const [drawStateActive, setDrawStateActive] = React.useState(false);
	const [invalidShapeDrawn, setInvalidShapeDrawn] = React.useState(false);
	const [completeShapeDrawn, setCompleteShapeDrawn] = React.useState(false);

	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	if (view) {
		view?.map.add(walkAreaGL);

		sketchVM = new SketchViewModel({
			view: view,
			layer: graphicsLayer,
			polylineSymbol: polylineSymbol,
			polygonSymbol: polygonSymbol,
		});

		// @ts-ignore
		sketchVM.on(["create", "update"], (evt: any) => {
			evt.state === "complete"
				? setDrawStateActive(false)
				: setDrawStateActive(true);
			let newGraphic: Graphic;
			let isLine = true;
			const toolEvtType = evt.toolEventInfo?.type;
			// get the created graphic and note whether it is a line or poly
			if (evt.graphic) {
				newGraphic = evt.graphic;
			} else {
				newGraphic = evt.graphics[0];
			}
			if (newGraphic.geometry.type === "polygon") {
				isLine = false;
			}

			if (city) {
				let validShape = true;
				let shape;
				let pointArray: any[] = [];
				// Get an array of points representing the shape either from the shape's paths (polyline) or rings (polygon)
				if (isLine) {
					shape = new Polyline(newGraphic.geometry);
					pointArray = shape.paths;
				} else {
					shape = new Polygon(newGraphic.geometry);
					pointArray = shape.rings;
				}

				// If any part of the shape is not contained within the city boundaries, classify the shape as invalid
				// Iterate over each line or polygon within the shape
				for (let i = 0; i < pointArray.length; i++) {
					const geo = pointArray[i];
					// If the shape has the minimum number of points to be complete
					// (for a polygon, at least four; for a polyline, at least two)
					// compare the entire shape with the city boundaries to determine if it is invalid
					if ((isLine && geo.length >= 2) || (!isLine && geo.length >= 4)) {
						setCompleteShapeDrawn(true);
						if (!geometryEngine.contains(city.geometry, newGraphic.geometry)) {
							validShape = false;
						}
						break;
					} else if (!isLine && geo.length === 3) {
						setCompleteShapeDrawn(false);
						// If the shape is a polygon with exactly three rings (the first and last point being the same),
						// the current geometry represents one side of a polygon. It is not complete but the side can be evaluated
						// to see if it is contained within the city boundaries. Extract the first two points and evaluate as a polyline
						let polygonSide = new Polyline({
							paths: geo.slice(0, 2),
							spatialReference: view.spatialReference,
						});
						if (!geometryEngine.contains(city.geometry, polygonSide)) {
							validShape = false;
						}
						break;
					}
					// If the current graphic geometry does not represent a complete shape
					// (a polygon with at least four points or a polyline with at least two points)
					// iterate over each point to determine if any are outside of city boundaries
					// For each polyline or polygon, iterate over the paths or rings
					for (let j = 0; j < geo.length; j++) {
						// Extract the coordinates and create a point object
						const coords = geo[j];
						const point = new Point({
							x: coords[0],
							y: coords[1],
							spatialReference: view.spatialReference,
						});
						// Compare the point object with the city geometry
						const containsPoint = geometryEngine.contains(city.geometry, point);
						if (!containsPoint) {
							validShape = false;
							// As soon as one invalid point is found, break the for loop
							break;
						}
					}
					if (!validShape) {
						// As soon as one invalid point is found, break the for loop
						break;
					}
				}

				// Depending on whether the shape is valid or invalid:
				// 1. Set the symbol to the invalid or valid symbol, switching on whether the graphic is a polyline or polygon
				// 2. If valid, update the graphics layer length
				// 3. Set the invalidShapeDrawn state to control whether the alert snackbar appears
				// 4. Undo the addition or reshape of a vertex if it is invalid
				if (validShape) {
					isLine
						? (newGraphic.symbol = polylineSymbol)
						: (newGraphic.symbol = polygonSymbol);
					setGraphicsLength(graphicsLayer.graphics.length);
					setInvalidShapeDrawn(false);
				} else {
					setInvalidShapeDrawn(true);
					isLine
						? (newGraphic.symbol = invalidPolylineSymbol)
						: (newGraphic.symbol = invalidPolygonSymbol);
					// If the tool from the event is "vertex-add", "reshape-stop", or "move-stop" (versus "cursor-update", "reshape", or "move"),
					// set a brief timeout to let the event conclude and then undo the latest action to the Sketch View Model,
					// reverting it to it's previous valid state
					switch (toolEvtType) {
						case "vertex-add":
						case "reshape-stop":
						case "move-stop":
							setTimeout(() => {
								sketchVM.undo();
							}, 0);
							break;
					}
				}

				// If the add or update event is complete, save the graphic length as the graphicsLength state
				if (evt.state === "complete") {
					setGraphicsLength(graphicsLayer.graphics.length);
					let parkOrTrail = isLine ? "trail" : "park";
					trackEvent("evaluate_draw", parkOrTrail);
				}
			}
		});
	}

	useEffect(() => {
		if (gpFailure && gpStartTime) {
			console.log("fail");
			let timeToExecute = Math.round((Date.now() - gpStartTime) / 1000);
			trackEvent("evaluate_analyze_status", { evaluate_analyze_status_result: "fail", evaluate_analyze_status_time: timeToExecute });
			setGpStartTime(null)
		}
		if (gpSuccess && gpStartTime) {
			console.log("success");
			let timeToExecute = Math.round((Date.now() - gpStartTime) / 1000);
			trackEvent("evaluate_analyze_status", { evaluate_analyze_status_result: "success", evaluate_analyze_status_time: timeToExecute });
			setGpStartTime(null)
		}
		if (gpCancel && gpStartTime) {
			console.log("cancel");
			let timeToExecute = Math.round((Date.now() - gpStartTime) / 1000);
			trackEvent("evaluate_analyze_status", { evaluate_analyze_status_result: "cancel", evaluate_analyze_status_time: timeToExecute });
			setGpStartTime(null)
		}

	}, [gpFailure, gpSuccess, gpCancel]);

	const handleDrawPark = () => {
		//trackEvaluate("park");
		sketchVM.create("polygon");
		setCompleteShapeDrawn(false);
	};

	const handleDrawTrail = () => {
		//trackEvaluate("trail");
		sketchVM.create("polyline");
	};

	const handleClear = (event: any) => {
		if (view) {
			setExecuted(false);
			graphicsLayer.removeAll();
			setGraphicsLength(0);
			walkAreaGL.removeAll();
			//@ts-ignore
			setGpArray(null);
			setGpJson?.(null);
			setExecuted(false);
			setCompleteShapeDrawn(false);
			setGpProgress(0);
		}
	};

	const cancelGpJob = () => {
		const jobUrl = `${gpURL}/jobs/${gpJobId}/cancel?f=json`;
		esriRequest(jobUrl).then(
			function (results) {
				setJobMessage("Cancelled request");
				setExecuted(false);
				setGpProgress(0);
				handleClose();
			},
			function (error) {
				console.log(error);
			}
		);
	};

	const getExecutionTime = (messages: any) => {
		let timeMessage = messages.pop();
		let time = timeMessage.description
			.split("Elapsed Time: ")
			.pop()
			.replace(" minutes", "")
			.replace(" seconds)", "")
			.split(" ")
			.map((t: any) => parseInt(t));

		let elapseInSeconds = 0;
		if (time.length > 1) {
			time.forEach((t: any, i: any) => {
				elapseInSeconds += i == 0 ? t * 60 : t;
			});
		} else {
			elapseInSeconds += time.pop();
		}

		return elapseInSeconds
	}

	const handleExecute = async () => {
		if (drawStateActive) {
			sketchVM.complete();
		}
		handleOpen();
		//trackEvaluateAnalyze();
		trackEvent("evaluate_analyze", "start_analysis");
		
		setExecuted(true);
		setJobMessage("Starting...");
		setGPSuccess(false);
		setGPFailure(false);
		setGPCancel(false);
		setGpProgress(0);
		setGpStartTime(Date.now())

		if (graphicsLayer.graphics.length > 0) {
			const inputParkArr: Graphic[] = [];
			const inputTrailsArr: Graphic[] = [];
			graphicsLayer.graphics.forEach((graphic: Graphic) => {
				if (graphic.geometry.type === "polygon") {
					inputParkArr.push(graphic);
				} else {
					inputTrailsArr.push(graphic);
				}
			});
			const inputParkFS = new FeatureSet();
			inputParkFS.features = inputParkArr;
			setParkArray?.(inputParkArr);
			const inputTrailsFS = new FeatureSet();
			inputTrailsFS.features = inputTrailsArr;
			setTrailArray?.(inputTrailsArr);

			const params: GPParams = {
				// @ts-ignore
				INPUT_PARK_FS: inputParkFS,
				// @ts-ignore
				INPUT_TRAILS_FS: inputTrailsFS,
				INPUT_PLACE_ID: city?.id,
			};

			// UNCOMMENT FOR TESTING ********************************************
			// setGpArray?.(gpArraySample);
			// setParkArray?.(parkArraySample);
			// setTrailArray?.(trailArraySample);
			// if (gpArraySample[2]?.fs.length > 0) {
			//   //@ts-ignore
			//   gpArraySample[2].fs.forEach((feature: any) => {
			//     const poly = new Polygon({
			//       rings: feature.geometry.rings,
			//       spatialReference: feature.geometry.spatialReference,
			//     })
			//     const graphic = new Graphic({
			//       symbol: walkAreaSymbol,
			//       geometry: poly
			//     })
			//     walkAreaGL.add(graphic);
			//   });
			// }
			// setAlignment("evaluate");
			// setGPSuccess(true);
			// ********************************************
			let currentProgress = 0;
			let currentStatus = "";
			// COMMENT FOR TESTING ********************************************
			const jobInfo: JobInfo = await geoprocessor.submitJob(gpURL, params);
			setGpJobId(jobInfo.jobId);

			const options = {
				interval: 3000,
				statusCallback: (j: any) => {
					if (j.messages.length) {
						setJobMessage(j.messages[j.messages.length - 1].description);
						if (
							j.messages[j.messages.length - 1].description !== currentStatus
						) {
							currentProgress += 4;
							currentStatus = j.messages[j.messages.length - 1].description;
							setGpProgress(Math.min(currentProgress, 99));
						}
					} else {
						setJobMessage("Unable to contact server.  Please wait or try again later.");
					}
				},
			};

			jobInfo
				.waitForJobCompletion(options)
				.then((resp: any) => {
					handleClose();
					getJobResultData(jobInfo);

					let elapseInSeconds = getExecutionTime(resp.messages);
					trackEvent("evaluate_analyze_time", elapseInSeconds);
				})
				.catch((e) => {
					console.log(e)

					if (e.jobStatus === "job-failed") {
						let errorMsg = "Unable to complete the analysis service.";
						const svcAreaMsgs = e.messages.filter(
							(m: any) => m.description === "No service areas created"
						);
						if (svcAreaMsgs.length === 2) {
							errorMsg =
								"Unable to generate a service area as there is no public access to the parks or trails submitted. Please make sure your parks or trails are within 100 feet of a public road.";
						}
						setJobMessage(errorMsg);
						setGPSuccess(false);
						setGPFailure(true);
						setGPCancel(false)
					}

					if (["job-cancelling", "job-cancelled"].includes(e.jobStatus)) {
						setGPSuccess(false);
						setGPFailure(false);
						setGPCancel(true)
					}
				});
			// ********************************************
		}

		const getJobResultData = (jobInfo: JobInfo) => {
			//console.log(jobInfo);
			const gpResults: any[] = [];
			let e: any;
			
			let outputParams = ["OUTPUT_ACCESS_POINTS_FS", "OUTPUT_SERVICE_AREAS_FS", "OUTPUT_JSON"]
			let fetchResults = outputParams.map((output: any) => {
				return jobInfo.fetchResultData(output)
			})
			Promise.all(fetchResults).then((response: any) => {
				let results: any = {}
				response.forEach((result: any, i: any) => {
					let paramName: any = outputParams[i]
					if (paramName === "OUTPUT_JSON") {
						["place", "total", "new"].forEach((key) => {

							let param = key === "place" ? "OUTPUT_PLACE_RS" : key === "total" ? "OUTPUT_TOTAL_RS" : "OUTPUT_NEW_RS";
							e = {
								paramName: param,
								fs: [{ attributes: replaceNullsWithZero(result.value[key], "_PCT") }],
							};
							results[param] = e
						})
					} else {
						e = {
							paramName: paramName,
							fs: result.value.features,
						};
						results[paramName] = e
					}
				})
				console.log(results)
				
				gpResults.push(results["OUTPUT_ACCESS_POINTS_FS"]);
				gpResults.push(results["OUTPUT_NEW_RS"]);
				gpResults.push(results["OUTPUT_SERVICE_AREAS_FS"]);
				gpResults.push(results["OUTPUT_TOTAL_RS"]);
				gpResults.push(results["OUTPUT_PLACE_RS"]);

				if (results["OUTPUT_SERVICE_AREAS_FS"].fs.length > 0) {
					//@ts-ignore
					results["OUTPUT_SERVICE_AREAS_FS"].fs.forEach((feature: any) => {
						const poly = new Polygon({
							rings: feature.geometry.rings,
							spatialReference: feature.geometry.spatialReference,
						});
						const graphic = new Graphic({
							symbol: walkAreaSymbol,
							geometry: poly,
						});
						walkAreaGL.add(graphic);
					});
					view?.goTo(results["OUTPUT_SERVICE_AREAS_FS"].fs);
				}

				setAlignment("evaluate");
				setGpArray?.(gpResults);
				setGpJson?.(results);
				setGPSuccess(true);
			})
	
		}
	};

	const replaceNullsWithZero = (obj: any, excludeKey: string) => {
		if (obj === null || typeof obj !== 'object') {
			return obj;
		}

		for (const key in obj) {
			const hasPct = key.includes(excludeKey);
			if (obj[key] === null && !hasPct) {
				obj[key] = 0;
			}
		}

		return obj;
	}

	return (
		<Box
			display="flex"
			flexDirection="column"
		>
			<Box
				display="flex"
				flexDirection="row"
				justifyContent="space-evenly"
				width="25vw"
				marginTop="30px"
				marginBottom="30px"
			>
				<Box
					display="flex"
					flexDirection="row"
				>
					<IconButton
						sx={{
							backgroundColor: "rgba(0,79,0,.3)",
							border: "1px",
							borderColor: "rgb(0,79,0)",
							"&:hover": { backgroundColor: "rgb(0,79,0)" },
							marginRight: "10px",
							height: "40px",
							width: "40px",
						}}
						aria-label="Draw a park"
						title="Draw a park"
						onClick={handleDrawPark}
						disabled={!city || executed}
					>
						<AddIcon />
					</IconButton>
					<Typography
						variant="body1"
						sx={{ lineHeight: "3" }}
					>
						Park
					</Typography>
				</Box>
				<Box
					sx={{
						borderLeft: "1px solid rgba(0, 0, 0, 0.26)",
						height: "50px",
					}}
				></Box>
				<Box
					display="flex"
					flexDirection="row"
				>
					<IconButton
						sx={{
							backgroundColor: "rgba(255, 165, 0,.3)",
							border: "1px",
							borderColor: "rgb(255, 165, 0)",
							"&:hover": { backgroundColor: "rgb(255, 165, 0)" },
							marginRight: "10px",
							height: "40px",
							width: "40px",
							".Mui-disabled": { backgroundColor: "rgb(79, 79, 79)" },
						}}
						aria-label="Draw a trail"
						title="Draw a trail"
						onClick={handleDrawTrail}
						disabled={!city?.feature || executed}
					>
						<AddIcon />
					</IconButton>
					<Typography
						variant="body1"
						sx={{ lineHeight: "3" }}
					>
						Trail
					</Typography>
				</Box>
			</Box>

			<Box
				display="flex"
				flexDirection="row"
				justifyContent="space-evenly"
				width="25vw"
				marginTop="30px"
				marginBottom="30px"
			>
				<Button
					variant="outlined"
					aria-label="Clear all parks and trails"
					disabled={graphicsLength === 0}
					title="Clear all parks and trails"
					onClick={handleClear}
					sx={{ width: "120px" }}
				>
					Start Over
				</Button>
				<Button
					aria-label="Run Analysis"
					onClick={handleExecute}
					disabled={
						graphicsLength === 0 ||
						executed ||
						invalidShapeDrawn ||
						!completeShapeDrawn
					}
					sx={{ width: "120px" }}
					variant="contained"
				>
					Run Analysis
				</Button>
			</Box>

			<Modal
				open={open}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box sx={style}>
					<Typography
						id="modal-modal-title"
						variant="h3"
						component="h2"
					>
						Analysis in progress...
					</Typography>
					<Typography
						id="modal-modal-description"
						sx={{ mt: 2 }}
					>
						{jobMessage}
					</Typography>
					{!gpFailure && (
						<Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
							<Box sx={{ width: "100%", mr: 1, mt: 2 }}>
								<LinearProgress
									variant="determinate"
									value={gpProgress}
									sx={{ width: "100%" }}
								/>
							</Box>
							<Box sx={{ minWidth: 35, mt: 2 }}>
								<Typography
									variant="body2"
									color="text.secondary"
								>{`${Math.round(gpProgress)}%`}</Typography>
							</Box>
						</Box>
					)}
					{!gpFailure && (
						<Typography
							id="body"
							sx={{ mt: 2 }}
						>
							This analysis may take a few minutes. The progress bar above shows
							approximate progress. Please do not refresh the page. To cancel
							the analysis, click the button below. If you choose to cancel,
							please note that you will lose all progress with the analysis.
						</Typography>
					)}

					{gpFailure ? (
						<Button
							onClick={() => {
								setExecuted(false);
								setGpProgress(0);
								setGPFailure(false);
								handleClose();
							}}
							variant="contained"
							sx={{ mt: 2 }}
						>
							OK
						</Button>
					) : (
						<Button
							onClick={cancelGpJob}
							variant="contained"
							sx={{ mt: 2 }}
						>
							Cancel
						</Button>
					)}
				</Box>
			</Modal>
			<Snackbar
				open={invalidShapeDrawn}
				// open
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="warning">
					This tool can only evaluate parks or trails drawn entirely within the
					boundaries of the selected city.
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default ParkTrailButtons;
